// The account bar under the record's head (brief section 4, items 2 to 4; drawing a6-account-file.html): billed in the
// last twelve months, the forecast for the next twelve, the potential from deals, and the dates, then the risk line and
// one verdict row per product read. Every figure and sentence is the one company read's own (RC1, U352), so the record
// says what the rundown and the line say, in the same words. Nothing here computes money.
import { ActionButton } from '../parts/index.js';
import type { CompanyRead } from './company-read';
import { gbp, plateDay, verdictRows, whose } from './company-read';

/** The "Also needs you" verdict row (brief section 4 item 4): how many of the reader's items are about the company, and their headlines. */
export interface NeedsRow { count: number; headlines: string[] }

/** "Send the court plan; Reply to Danny; and 3 more": the first three headlines, then how many more. */
export function needsSentence(n: NeedsRow): string {
  const first = n.headlines.slice(0, 3);
  return `${first.join('; ')}${n.count > first.length ? `; and ${n.count - first.length} more` : ''}`;
}

export function AccountBar({ read, open, needs = null, addDeal = null }: { read: CompanyRead; open: (path: string) => void; needs?: NeedsRow | null; addDeal?: string | null }) {
  const { billed, forecast } = read.money;
  // A company finance holds no line for (drawing c5-states.html, "A prospect with nothing on record yet"): one cell says
  // nothing is billed and so nothing is forecast, rather than two cells of nothing.
  if (!read.money.available) {
    return (
      <div className="abar" aria-label="The account">
        <div className="abar-cells">
          <div className="abar-cell">
            <span className="k">Billed, last 12 months</span>
            <span className="fig word">Nothing billed</span>
            <span className="meta">Finance holds no line for them, so there is no forecast either</span>
          </div>
          <Potential read={read} addDeal={addDeal} open={open} />
          <Dates read={read} />
        </div>
        <Verdicts read={read} needs={needs} />
      </div>
    );
  }
  return (
    <div className="abar" aria-label="The account">
      <div className="abar-cells">
        <div className="abar-cell">
          <span className="k">{billed.words.label}</span>
          {billed.total !== null
            ? <span className="fig">{gbp(billed.total)}</span>
            : <span className="fig word">Nothing billed</span>}
          {billed.words.note ? <span className="meta">{billed.words.note}</span> : null}
        </div>
        <div className="abar-cell">
          <span className="k">Forecast, next 12 months</span>
          {forecast.total !== null
            ? <span className="fig">{gbp(forecast.total)}</span>
            : <span className="fig word">No forecast</span>}
          {forecast.words.note ? <span className="meta">{forecast.words.note}</span> : null}
          {forecast.words.ifEnds ? <span className="meta">{forecast.words.ifEnds}</span> : null}
          {forecast.words.notCounted ? <span className="meta">{forecast.words.notCounted}</span> : null}
        </div>
        <Potential read={read} addDeal={addDeal} open={open} />
        <Dates read={read} />
      </div>
      {read.risk ? (
        <div className="abar-risk">
          {/* The read's sentence: its first, the share and the date, in bold; the deal that keeps it after. */}
          <p>{(() => { const [first, ...rest] = read.risk!.words.split(/(?<=\.)\s+/); return <><b>{first}</b>{rest.length ? <> {rest.join(' ')}</> : null}</>; })()}</p>
          {read.risk.deal ? (
            <div className="opts">
              <ActionButton reading type="button" className="opt lead" onClick={() => open(`/deals/${read.risk!.deal!.id}`)}>
                <span className="l">Open the deal</span>
                <small>{read.risk.deal.onThisRecord ? read.risk.deal.title : `on ${whose(read.risk.deal.organisationName)} record`}</small>
              </ActionButton>
            </div>
          ) : null}
        </div>
      ) : null}
      <Verdicts read={read} needs={needs} />
    </div>
  );
}

/** Potential from deals; where the company has no deal and nothing heard, "No deal yet" and Add a deal with it set (c5-states.html). */
function Potential({ read, addDeal, open }: { read: CompanyRead; addDeal: string | null; open: (path: string) => void }) {
  const { potential } = read.money;
  const none = potential.deals === 0 && potential.proposals.length === 0;
  return (
    <div className="abar-cell">
      <span className="k">{potential.words.label}</span>
      {potential.total !== null && potential.total > 0
        ? <span className="fig">{gbp(potential.total)}</span>
        : <span className="fig word">{none ? 'No deal yet' : potential.deals ? 'No figure on a deal' : 'None on a deal'}</span>}
      {potential.words.note ? <span className="meta">{potential.words.note}</span> : null}
      {none && addDeal
        ? <span className="opts"><ActionButton type="button" className="opt lead" onClick={() => open(addDeal)}><span className="l">Add a deal</span><small>with {read.company.name} set</small></ActionButton></span>
        : null}
    </div>
  );
}

/** The dates in plates, red only within 90 days of an agreement's end; "No agreement with them." where none is on the register. */
function Dates({ read }: { read: CompanyRead }) {
  return (
    <div className="abar-cell">
      <span className="k">Dates</span>
      {read.dates.length === 0
        ? <span className="meta">{read.agreements.inForce.length === 0 ? 'No agreement with them.' : 'No notice point or end in the next twelve months'}</span>
        : read.dates.map((d) => (
          <span className="abar-date" key={`${d.kind}${d.on}${d.words}`}>
            <span className="plate">{d.red ? <i className="dot" aria-label="within 90 days of its end" /> : null}{plateDay(d.on)}</span>
            <span className="meta">{d.words}</span>
          </span>
        ))}
    </div>
  );
}

function Verdicts({ read, needs }: { read: CompanyRead; needs: NeedsRow | null }) {
  const verdicts = verdictRows(read);
  if (!verdicts.length && !(needs && needs.count)) return null;
  return (
    <ul className="abar-verdicts">
      {verdicts.map((v) => (
        <li key={v.product}>
          <a href={`#${v.anchor}`}>{v.product}</a>
          <span className="level">{v.chip}</span>
          <span>{v.sentence}</span>
        </li>
      ))}
      {needs && needs.count ? (
        <li key="needs">
          <a href="#a-needs">Also needs you</a>
          <span className="level">{needs.count}</span>
          <span>{needsSentence(needs)}</span>
        </li>
      ) : null}
    </ul>
  );
}
